import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import firebase from 'firebase/app';
import qs from 'qs';

import { clearSessionForDashboard } from '../client/auth/ClientLoginScreen';
import Redirect404 from '../../components/Redirect404';
import { ClaimTagComponent } from '../../components/customer/ClaimTagComponents';
import CenteredSpinner from '../../components/CenteredSpinner';
import TrackerService from '../../utils/TrackerService';
import { useCurrentUser } from '../../models/useCurrentUser';
import { useTag } from '../../models/useTag';
import { useTemplate } from '../../models/useTemplate';
import { useBrandProduct } from '../../contexts/BrandContext';

import '../../App.scss';
import 'antd/dist/antd.css';

const BeginClaimScreen = ({ locStrings }) => {
  const { tagId } = useParams();
  const location = useLocation();
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const customUrl = queryParams.url;

  // 1. Handle Auth (Anonymous if needed)
  const [authLoading, user] = useCurrentUser({ signInAnonymously: true });

  // 2. Listen to Tag live
  const [tagLoading, rawTag] = useTag(tagId);

  // 3. Listen to Template live (only once tag is available)
  const [templateLoading, template] = useTemplate(rawTag?.templateTagId);

  // 4. Merge birth tag with live template data
  const liveTag = useMemo(() => {
    if (!rawTag) return null;
    return { ...rawTag, ...template, id: rawTag.id };
  }, [rawTag, template]);

  // 5. Brand: set product for header/footer (tipping→striketip, payment/giving→strikepay)
  const { setProduct: setBrandProduct } = useBrandProduct();
  const productFromTagOrTemplate = rawTag?.product ?? template?.product;
  useEffect(() => {
    setBrandProduct(productFromTagOrTemplate ?? null);
    return () => setBrandProduct(null);
  }, [productFromTagOrTemplate, setBrandProduct]);

  const [proceedToRoute, setProceedToRoute] = useState(null);
  const [returnFromRoute, setReturnFromRoute] = useState(null);

  const makeFullUrlWithRoute = useCallback((route, params, isNotEncoded) => {
    const queryParts = [];
    for (let [key, value] of Object.entries(params)) {
      if (value) {
        if (!isNotEncoded) {
          queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        } else {
          queryParts.push(`${key}=${value}`);
        }
      }
    }
    let queryString = queryParts.join('&');
    if (queryString.length > 0) {
      queryString = `?${queryString}`;
    }
    return `${route}${queryString}`;
  }, []);

  const getProceedToRoute = useCallback((intent, options = {}) => {
    let pRoute = null;
    let rRoute = null;
    let params = {};
    const goBackRoute = '/';
    let redirectPath = `/client/claim/${tagId}`;
    if (customUrl) redirectPath += `?url=${encodeURIComponent(customUrl)}`;

    if (tagId) params = { ...params, redirect: redirectPath };

    const proceedToClaimRoute = makeFullUrlWithRoute('/client/login', params);
    const proceedToSignupRoute = makeFullUrlWithRoute('/client/signup', params);
    // Dashboard: go to login with no redirect params so user lands on dashboard, not back on claim page
    const proceedToDashboardRoute = '/client/login';

    switch (intent) {
      case 'claim': pRoute = proceedToClaimRoute; break;
      case 'signup': pRoute = proceedToSignupRoute; break;
      case 'signin': pRoute = proceedToClaimRoute; break;
      case 'cancel': pRoute = goBackRoute; break;
      case 'dashboard':
        if (options.fromClick) clearSessionForDashboard();
        pRoute = proceedToDashboardRoute;
        rRoute = tagId ? `/tags/${tagId}` : null;
        break;
      default: break;
    }
    return { pRoute, rRoute };
  }, [tagId, customUrl, makeFullUrlWithRoute]);

  const onAction = useCallback((intent, options = {}) => {
    const { pRoute, rRoute } = getProceedToRoute(intent, options);
    setProceedToRoute(pRoute);
    setReturnFromRoute(rRoute);
  }, [getProceedToRoute]);

  // 4. Handle Auto-Redirects
  useEffect(() => {
    if (liveTag && user && liveTag.status === 'live' && liveTag.ownerId === user.uid) {
      onAction('dashboard');
    }
  }, [liveTag, user, onAction]);

  // 5. Track claim_started when user sees claim CTA (not when they already own the tag)
  const claimStartedTracked = useRef(false);
  useEffect(() => {
    if (!liveTag || !user || claimStartedTracked.current) return;
    if (liveTag.status === 'live' && liveTag.ownerId === user.uid) return;
    claimStartedTracked.current = true;
    const tracker = new TrackerService({ tagId, object: liveTag });
    const payload = TrackerService.buildClaimPayload(liveTag, null, user.uid, { step: 'started' });
    tracker.mixpanelTrack('claim_started', payload);
    if (!tracker.state.isTest) tracker.googleTrack('claim_started', payload);
  }, [liveTag, user, tagId]);

  const isProvisioning = (authLoading || tagLoading || (rawTag && templateLoading)) && !liveTag;
  const isTagMissing = !tagLoading && !rawTag;

  if (isProvisioning) {
    return <CenteredSpinner loading={true} noLayout={true} tip="Provisioning your tag..." />;
  }

  if (proceedToRoute) {
    return <Redirect to={proceedToRoute} from={returnFromRoute} />;
  }

  if (liveTag && user) {
    return (
      <ClaimTagBody
        loading={false}
        user={user}
        tagId={tagId}
        tag={liveTag}
        onAction={onAction}
        locStrings={locStrings}
      />
    );
  }

  if (isTagMissing) {
    return <Redirect404 fromPath={location.pathname} reason={"begin-claim-failed"} />;
  }

  return <CenteredSpinner loading={true} noLayout={true} />;
};

export function ClaimTagBody({ tagId, tag, user, loading, onAction, locStrings }) {
  const baseStyle = {
    width: '100%',
    minWidth: '260px',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    padding: 0,
    margin: 0
  };

  return (
    <div className="AppScreen BeginClaimScreen" style={baseStyle}>
      <ClaimTagComponent
        loading={loading}
        user={user}
        tagId={tagId}
        tag={tag}
        onAction={onAction}
        locStrings={locStrings}
      />
    </div>
  );
}

export default BeginClaimScreen;
